import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from 'react-router-dom';

import { Notification } from "@/types";
import { clearNotification, clearAllNotifications, getNotifications } from "@/api";

export const useNotifications = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  const client = process.env.REACT_APP_CLIENT || 'core';

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const { data, error } = await getNotifications(accessToken, client);

      // Keep the current list if the request fails
      if (error) setError(error.message);
      else setNotifications(data ?? []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [client, getAccessTokenSilently]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      const accessToken = await getAccessTokenSilently();
      const { data, error } = await clearNotification(accessToken, client, id);

      if (error) setError(error.message);
      else setNotifications(data ?? []);

    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [client, getAccessTokenSilently]);

  const markAllAsRead = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const { data, error } = await clearAllNotifications(accessToken, client);

      if (error) setError(error.message);
      else setNotifications(data ?? []);

    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [client, getAccessTokenSilently]);


  useEffect(() => {

    // Get Notifications when landing on the page
    fetchNotifications();

    // Add a handler to get Notifications when the user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup listeners on unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }

  }, [location.pathname, getAccessTokenSilently, fetchNotifications]);

  return useMemo(() => ({
    notifications,
    refresh: fetchNotifications,
    markAsRead,
    markAllAsRead,
    loading,
    error,
  }), [notifications, fetchNotifications, markAsRead, markAllAsRead, loading, error]);
};
